/**
 * Video Watch Tracker Integration
 *
 * This utility helps integrate the GTM video watch tracker with the React application.
 * It listens for the custom event dispatched by the GTM script and shows the login modal.
 *
 * Usage:
 * Import this file in your main app component or layout component:
 *
 * import { initVideoWatchTrackerIntegration } from '@/utils/video-watch-tracker-integration';
 *
 * // In your component's useEffect:
 * useEffect(() => {
 *   const cleanup = initVideoWatchTrackerIntegration(setShowLogin);
 *   return cleanup;
 * }, []);
 *
 * When user dismisses the modal without logging in:
 * - Call resumeVideoAfterDismiss() in your modal close handler
 */

/**
 * Initialize the video watch tracker integration
 * @param {Function} setShowLogin - Function to set the login modal state to true
 * @returns {Function} Cleanup function to remove event listeners
 */
export function initVideoWatchTrackerIntegration(setShowLogin) {
  const handleShowLoginModal = (event) => {
    console.log('Login modal triggered by video watch tracker:', event.detail);

    // Show the login modal
    if (typeof setShowLogin === 'function') {
      setShowLogin(true);
    }
  };

  // Listen for the custom event from GTM script
  window.addEventListener('showLoginModal', handleShowLoginModal);

  // Return cleanup function
  return () => {
    window.removeEventListener('showLoginModal', handleShowLoginModal);
  };
}

/**
 * Resume video playback after user dismisses the login modal
 * Call this when the user closes the modal without logging in
 */
export function resumeVideoAfterDismiss() {
  if (window.videoWatchTracker && typeof window.videoWatchTracker.resumeVideo === 'function') {
    window.videoWatchTracker.resumeVideo();
    console.log('Video resumed after dismissing login modal');
  }
}

/**
 * Reset the video watch tracker (call after successful login)
 */
export function resetVideoWatchTracker() {
  if (window.videoWatchTracker && typeof window.videoWatchTracker.resetWatchTime === 'function') {
    window.videoWatchTracker.resetWatchTime();
    console.log('Video watch tracker reset after login');
  }
}

/**
 * Get current watch time (for debugging)
 */
export function getVideoWatchTime() {
  if (window.videoWatchTracker && typeof window.videoWatchTracker.getWatchTime === 'function') {
    return window.videoWatchTracker.getWatchTime();
  }
  return 0;
}

/**
 * Check if user is logged in according to the tracker
 */
export function isUserLoggedInForTracker() {
  if (window.videoWatchTracker && typeof window.videoWatchTracker.isUserLoggedIn === 'function') {
    return window.videoWatchTracker.isUserLoggedIn();
  }
  return false;
}
